/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  Menu,
  Category,
  Item,
  Department,
  SubDepartment,
  ItemClass,
  MenuTranslationSetting,
} from "types/models/menu"
import { ItemFilterType } from "components/dropdowns/ItemPhotoSelect"
import { unifyHiddenStatus } from "utils/item_status"
import { getNormalizedSellingTimes } from "./selling_time_helper"

export const getSellingTimeList = (menu: Menu) =>
  getNormalizedSellingTimes(menu.sellingTimes)

export const getCategoryList = (menu: Menu): Category[] =>
  menu?.categories ??
  menu?.sections?.reduce(
    (acc, section) => [
      ...acc,
      ...(section?.categories?.map(category => ({
        ...category,
        sectionName: section.sectionName,
        sectionID: section.sectionID,
      })) ?? []),
    ],
    [],
  ) ??
  []

export const getItemList = (menu: Menu): Item[] => {
  const categories = getCategoryList(menu)
  return (
    categories?.reduce(
      (acc, category) => [
        ...acc,
        ...(category?.items?.map(item => ({
          ...item,
          availableStatus: unifyHiddenStatus(item.availableStatus),
          sectionName: category.sectionName,
          sectionID: category.sectionID,
          categoryName: category.categoryName,
          categoryID: category.categoryID,
        })) ?? []),
      ],
      [],
    ) ?? []
  )
}

export const getMartDepartmentList = (menu: Menu): Department[] =>
  menu?.departments ??
  menu?.sections?.reduce(
    (acc, section) => [
      ...acc,
      ...(section?.departments?.map(department => ({
        ...department,
        sectionName: section.sectionName,
        sectionID: section.sectionID,
      })) ?? []),
    ],
    [],
  ) ??
  []

export const getMartSubDepartmentList = (menu: Menu): SubDepartment[] => {
  const departments = getMartDepartmentList(menu)
  return (
    departments?.reduce(
      (acc, department) => [
        ...acc,
        ...(department?.subDepartments?.map(subDepartment => ({
          ...subDepartment,
          sectionName: department.sectionName,
          sectionID: department.sectionID,
          departmentName: department.itemClassName,
          departmentID: department.itemClassID,
        })) ?? []),
      ],
      [],
    ) ?? []
  )
}

export const getMartItemList = (menu: Menu): Item[] => {
  const subDepartments = getMartSubDepartmentList(menu)
  return (
    subDepartments?.reduce(
      (acc, subDepartment) => [
        ...acc,
        ...(subDepartment?.items?.map(item => ({
          ...item,
          availableStatus: unifyHiddenStatus(item.availableStatus),
          sectionName: subDepartment.sectionName,
          sectionID: subDepartment.sectionID,
          departmentName: subDepartment.departmentName,
          departmentID: subDepartment.departmentID,
          subDepartmentName: subDepartment.itemClassName,
          subDepartmentID: subDepartment.itemClassID,
        })) ?? []),
      ],
      [],
    ) ?? []
  )
}

/**
 * Enriches search result items by mapping itemClass fields for search item response
 */
export const enrichSearchResultItems = (items: Item[]): Item[] => {
  return items.map(item => ({
    ...item,
    availableStatus: unifyHiddenStatus(item.availableStatus),
    departmentID: item.parentItemClassID,
    departmentName: item.parentItemClassName,
    subDepartmentID: item.itemClassID,
    subDepartmentName: item.itemClassName,
  }))
}

export const mapItemClassToDepartment: (itemClass: ItemClass) => Department = (
  itemClass: ItemClass,
) => ({
  itemClassID: itemClass.ItemClassID,
  itemClassName: itemClass.Name,
  departmentID: itemClass.ParentID,
  subDepartments: [],
})

export const getCategoryByID = (
  categoryID?: string,
  categories?: Category[],
) => {
  return categories?.find(category => category.categoryID === categoryID)
}

export const getSubDepartmentByID = (
  subDepartmentID?: string,
  subDepartments?: SubDepartment[],
) => {
  return subDepartments?.find(
    subDepartment => subDepartment.itemClassID === subDepartmentID,
  )
}

export const filterDepartmentsForGrabCategory = (itemClasses: ItemClass[]) => {
  return itemClasses?.filter(itemClass => itemClass.ParentID === "")
}

export const filteredSubDepartmentsForGrabCategory = (
  itemClasses: ItemClass[],
) => {
  return itemClasses?.filter(itemClass => itemClass.ParentID !== "")
}

export const isMenuSupportedLanguages = (
  languageSettings: MenuTranslationSetting | undefined,
) => {
  return !!languageSettings?.menuSupportedLanguages?.length
}

export const getItemWithoutPhotoCount = (items: Item[]): number => {
  return items.filter(
    item => !(item.imageURL || (item.imageURLs && item.imageURLs.length > 0)),
  ).length
}

export const getItemWithoutPhotoCountFromDepartment = (
  department: Department,
): number => {
  let total = 0
  department.subDepartments.forEach(subDepartment => {
    total += getItemWithoutPhotoCount(subDepartment.items)
  })
  return total
}

export const isItemWithoutPhotoMode = (
  selectedFilterValue: ItemFilterType,
): boolean => {
  return selectedFilterValue === "itemsWithoutPhoto"
}
